import {
    getAssetRelativePathFromPath,
    getDocumentAssetsDir,
    isDocumentAssetsPath,
} from "./publish-utils";
import { workspaceFs } from "./engine-fs";

interface AssetTreeLike {
    name: string;
    path: string;
    isDirectory: boolean;
    children?: AssetTreeLike[];
}

export interface AssetReferenceEntry {
    name: string;
    relativePath: string;
    isDirectory: boolean;
    namespace?: string;
}

export function collectDocumentAssetReferences(
    fileTree: AssetTreeLike[],
    documentPath: string | null | undefined,
    namespace?: string,
): AssetReferenceEntry[] {
    if (!documentPath) return [];
    const assetsDir = normalizePath(getDocumentAssetsDir(documentPath));
    const entries: AssetReferenceEntry[] = [];

    const visit = (node: AssetTreeLike) => {
        const nodePath = normalizePath(node.path);
        if (
            nodePath !== assetsDir &&
            nodePath.startsWith(`${assetsDir}/`) &&
            isDocumentAssetsPath(nodePath)
        ) {
            const relativePath = getAssetRelativePathInDir(node.path, assetsDir);
            if (relativePath) {
                entries.push({
                    name: node.name,
                    relativePath,
                    isDirectory: node.isDirectory,
                    ...(namespace ? { namespace } : {}),
                });
            }
        }
        for (const child of node.children ?? []) visit(child);
    };

    for (const node of fileTree) visit(node);
    return sortAssetEntries(entries);
}

export async function loadDocumentAssetReferences(
    documentPath: string | null | undefined,
    namespace?: string,
): Promise<AssetReferenceEntry[]> {
    if (!documentPath) return [];
    const assetsDir = getDocumentAssetsDir(documentPath);
    const entries: AssetReferenceEntry[] = [];

    const visitDir = async (dirPath: string) => {
        const children = await workspaceFs.readOptionalDir(dirPath);
        for (const child of children) {
            const relativePath = getAssetRelativePathInDir(child.path, assetsDir);
            if (relativePath) {
                entries.push({
                    name: child.name,
                    relativePath,
                    isDirectory: child.isDirectory,
                    ...(namespace ? { namespace } : {}),
                });
            }
            if (child.isDirectory) {
                await visitDir(child.path);
            }
        }
    };

    try {
        await visitDir(assetsDir);
    } catch {
        return [];
    }

    return sortAssetEntries(entries);
}

function normalizePath(path: string): string {
    return path.replace(/\\/g, "/");
}

function getAssetRelativePathInDir(
    filePath: string,
    assetsDir: string,
): string | null {
    const normalizedFilePath = normalizePath(filePath);
    const normalizedAssetsDir = normalizePath(assetsDir);
    if (!normalizedFilePath.startsWith(`${normalizedAssetsDir}/`)) {
        return getAssetRelativePathFromPath(filePath);
    }
    const relativePath = normalizedFilePath.slice(normalizedAssetsDir.length + 1);
    return relativePath.length > 0 ? relativePath : null;
}

function sortAssetEntries(entries: AssetReferenceEntry[]): AssetReferenceEntry[] {
    return [...entries].sort((a, b) => {
        if (a.isDirectory !== b.isDirectory) return a.isDirectory ? -1 : 1;
        return a.relativePath.localeCompare(b.relativePath);
    });
}
